import { Notice, Badge, type BadgeTone } from "@conuti-das/prince-ui";
import { Flex, Stack } from "@/ui-prince";
import type { ValidationFinding } from "@/api/validatorApi";

interface Props {
  errors: ValidationFinding[];
  warnings: ValidationFinding[];
  infos: ValidationFinding[];
}

type Severity = "error" | "warning" | "info";

const SEVERITY_BADGE_TONE: Record<Severity, BadgeTone> = {
  error: "red",
  warning: "orange",
  info: "blue",
};

const SEVERITY_BORDER_COLOR: Record<Severity, string> = {
  error: "var(--sapNegativeColor, #bb0000)",
  warning: "var(--sapCriticalColor, #e9730c)",
  info: "var(--sapInformativeColor, #0064d9)",
};

function FindingRow({
  severity,
  f,
}: {
  severity: Severity;
  f: ValidationFinding;
}) {
  const loc = f.location;
  const locStr =
    `${loc.segment_tag}` +
    (loc.data_element ? ` DE${loc.data_element}` : "") +
    (loc.value ? `=${loc.value}` : "") +
    ` (#${loc.segment_index})`;

  return (
    <div
      style={{
        padding: "var(--s-2) var(--s-3)",
        gap: "var(--s-1)",
        borderLeft: `4px solid ${SEVERITY_BORDER_COLOR[severity]}`,
        background: "var(--sapList_Background)",
        marginBottom: "var(--s-1)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
        <Badge tone={SEVERITY_BADGE_TONE[severity]} variant="solid">
          {f.code}
        </Badge>
        <span
          style={{
            fontFamily: "var(--sapFontMonoFamily, monospace)",
            color: "var(--sapContent_LabelColor)",
            fontSize: "0.8rem",
          }}
        >
          {locStr}
        </span>
      </Flex>
      <span>{f.message_de}</span>
      {f.condition_key && (
        <span
          style={{
            fontFamily: "var(--sapFontMonoFamily, monospace)",
            fontSize: "0.8rem",
            color: "var(--sapContent_LabelColor)",
          }}
        >
          Bedingung [{f.condition_key}]
        </span>
      )}
    </div>
  );
}

export function FindingsList({ errors, warnings, infos }: Props) {
  const empty = errors.length === 0 && warnings.length === 0 && infos.length === 0;

  if (empty) {
    return <Notice tone="positive">Keine Beanstandungen.</Notice>;
  }

  return (
    <Stack style={{ gap: "var(--s-3)" }}>
      {errors.length > 0 && (
        <Stack style={{ gap: "var(--s-1)" }}>
          <h5 style={{ margin: 0, fontSize: "0.95rem" }}>Fehler ({errors.length})</h5>
          {errors.map((e, i) => (
            <FindingRow key={i} severity="error" f={e} />
          ))}
        </Stack>
      )}
      {warnings.length > 0 && (
        <Stack style={{ gap: "var(--s-1)" }}>
          <h5 style={{ margin: 0, fontSize: "0.95rem" }}>Warnungen ({warnings.length})</h5>
          {warnings.map((w, i) => (
            <FindingRow key={i} severity="warning" f={w} />
          ))}
        </Stack>
      )}
      {infos.length > 0 && (
        <Stack style={{ gap: "var(--s-1)" }}>
          <h5 style={{ margin: 0, fontSize: "0.95rem" }}>Infos ({infos.length})</h5>
          {infos.map((info, idx) => (
            <FindingRow key={idx} severity="info" f={info} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
